/**
 * The admin console's numbers, money, Lux and dates in the reader's language (docs/contributing/i18n.md, "Formatting"):
 * the same shapes as lib/format.ts and lib/lux.ts, formatted with react-intl's locale instead of the browser's or
 * en-US. Only the admin pages use it.
 */
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useT } from "../../i18n/I18nProvider";

const DASH = "—";

export function useFmt() {
  const intl = useIntl();
  const t = useT();
  return useMemo(() => {
    const num = (value: number | null | undefined, digits = 0): string =>
      value == null ? DASH : intl.formatNumber(value, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    const money = (value: number | null | undefined, currency = "USD", digits = 0): string =>
      value == null ? DASH : intl.formatNumber(value, { style: "currency", currency, minimumFractionDigits: digits, maximumFractionDigits: digits });
    /** Minor units (cents) as money with two decimals. */
    const cents = (minor: number | null | undefined, currency = "USD"): string => (minor == null ? DASH : money(minor / 100, currency, 2));
    /** Lux in hundredths ("12.50"). */
    const lux = (hundredths: number | null | undefined): string => (hundredths == null ? DASH : num(Math.trunc(hundredths) / 100, 2));
    /** A percentage given as a number of percent (20 → "20 %"). */
    const percent = (value: number | null | undefined, digits = 0): string =>
      value == null ? DASH : intl.formatNumber(value / 100, { style: "percent", minimumFractionDigits: 0, maximumFractionDigits: digits });
    const date = (value: string | null | undefined, withTime = false): string => {
      if (!value) return DASH;
      // a calendar date ("2025-08-01") is that day wherever the reader is, not UTC midnight
      const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
      if (day) return intl.formatDate(new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])), { year: "numeric", month: "short", day: "numeric" });
      return withTime
        ? intl.formatDate(value, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
        : intl.formatDate(value, { year: "numeric", month: "short", day: "numeric" });
    };
    const relative = (value: string | null | undefined): string => {
      if (!value) return t("admin.common.never");
      const seconds = (Date.now() - new Date(value).getTime()) / 1000;
      if (seconds < 60) return intl.formatRelativeTime(0, "second", { numeric: "auto" });
      if (seconds < 3600) return intl.formatRelativeTime(-Math.floor(seconds / 60), "minute", { style: "short" });
      if (seconds < 86400) return intl.formatRelativeTime(-Math.floor(seconds / 3600), "hour", { style: "short" });
      if (seconds < 86400 * 30) return intl.formatRelativeTime(-Math.floor(seconds / 86400), "day", { style: "short" });
      return date(value);
    };
    return { num, money, cents, lux, percent, date, relative };
  }, [intl, t]);
}

export type Fmt = ReturnType<typeof useFmt>;
